import React, { useState } from 'react';
import { X, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EnquiryModal = ({ isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    budget: '',
    timeline: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/enquiries`, formData);
      toast.success('Enquiry submitted successfully! Our team will get back to you within 24 hours.');
      onClose();
      setFormData({ name: '', email: '', phone: '', company: '', service: '', budget: '', timeline: '', message: '' });
    } catch (error) {
      console.error('Enquiry error:', error);
      toast.error('Failed to submit enquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-[#121212] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#121212] border-b border-white/10 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-white">Send Enquiry</h2>
            <p className="text-white/60 mt-1">Tell us about your project requirements</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-white text-sm font-medium mb-2">Full Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-[#06B6D4] transition-colors"
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="block text-white text-sm font-medium mb-2">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-[#06B6D4] transition-colors"
                placeholder="john@company.com"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-white text-sm font-medium mb-2">Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-[#06B6D4] transition-colors"
                placeholder="+91 98765 43210"
              />
            </div>
            <div>
              <label className="block text-white text-sm font-medium mb-2">Company Name</label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-[#06B6D4] transition-colors"
                placeholder="Your Company"
              />
            </div>
          </div>

          <div>
            <label className="block text-white text-sm font-medium mb-2">Service Required *</label>
            <select
              name="service"
              value={formData.service}
              onChange={handleInputChange}
              required
              className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-[#06B6D4] transition-colors"
            >
              <option value="" className="bg-[#121212]">Select a service</option>
              <option value="ai-agent" className="bg-[#121212]">AI Agent Development</option>
              <option value="llm" className="bg-[#121212]">LLM Deployment in Workflow</option>
              <option value="fullstack" className="bg-[#121212]">Full Stack Development</option>
              <option value="mobile" className="bg-[#121212]">Mobile App Development</option>
              <option value="ecommerce" className="bg-[#121212]">Ecommerce Development (Shopify/WooCommerce)</option>
              <option value="erp" className="bg-[#121212]">ERP Implementation</option>
              <option value="wms" className="bg-[#121212]">WMS Implementation</option>
              <option value="aws" className="bg-[#121212]">AWS Cloud Services</option>
              <option value="supply-chain" className="bg-[#121212]">Supply Chain Consultancy & Digitization</option>
              <option value="other" className="bg-[#121212]">Other Services</option>
            </select>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-white text-sm font-medium mb-2">Budget Range</label>
              <select
                name="budget"
                value={formData.budget}
                onChange={handleInputChange}
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-[#06B6D4] transition-colors"
              >
                <option value="" className="bg-[#121212]">Select budget range</option>
                <option value="50k-1L" className="bg-[#121212]">₹50,000 - ₹1,00,000</option>
                <option value="1L-2.5L" className="bg-[#121212]">₹1,00,000 - ₹2,50,000</option>
                <option value="2.5L-5L" className="bg-[#121212]">₹2,50,000 - ₹5,00,000</option>
                <option value="5L-10L" className="bg-[#121212]">₹5,00,000 - ₹10,00,000</option>
                <option value="10L+" className="bg-[#121212]">₹10,00,000+</option>
              </select>
            </div>
            <div>
              <label className="block text-white text-sm font-medium mb-2">Timeline</label>
              <select
                name="timeline"
                value={formData.timeline}
                onChange={handleInputChange}
                className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-[#06B6D4] transition-colors"
              >
                <option value="" className="bg-[#121212]">Select timeline</option>
                <option value="1-2months" className="bg-[#121212]">1-2 Months</option>
                <option value="3-6months" className="bg-[#121212]">3-6 Months</option>
                <option value="6-12months" className="bg-[#121212]">6-12 Months</option>
                <option value="ongoing" className="bg-[#121212]">Ongoing Support</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-white text-sm font-medium mb-2">Project Details *</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              required
              rows={4}
              className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-[#06B6D4] transition-colors resize-none"
              placeholder="Describe your project requirements, goals, and any specific needs..."
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary w-full disabled:opacity-50"
          >
            <Send className="w-5 h-5" />
            {isSubmitting ? 'Submitting...' : 'Submit Enquiry'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EnquiryModal;
